/**
 * One row of the per-pool notifications screen, in one of two visual
 * states: quiet, following the account settings, or full contrast, with an
 * override. `row.isOverridden` is server-sent and never inferred here.
 *
 * This component holds no state, performs no network call, and imports no
 * generated hook. Every value and every handler arrives as a prop.
 */
import { Button, Checkbox, cn } from "@sportsmet/ui";
import type { ReactNode } from "react";

import type { typeChannel } from "../notifications/alertKindLabels";
import {
  POOL_NOTIFICATIONS_FOLLOWING_CAPTION,
  POOL_NOTIFICATIONS_RESET_LABEL,
  type typePoolAlertRowModel,
} from "./poolNotificationsState";

export const PoolAlertRow = ({
  row,
  pendingChannel,
  isResetPending,
  onToggleChannel,
  onReset,
}: {
  row: typePoolAlertRowModel;
  /** The one channel on THIS row with a write in flight, or null. */
  pendingChannel: typeChannel | null;
  isResetPending: boolean;
  onToggleChannel: (input: {
    channel: typeChannel;
    isEnabled: boolean;
  }) => void;
  onReset: () => void;
}): ReactNode => {
  return (
    <div
      data-testid={`pool-alert-row-${row.alertKind}`}
      data-overridden={row.isOverridden ? "true" : undefined}
      className={cn(
        "flex flex-col gap-2 border-t border-hairline py-3 sm:flex-row sm:items-center sm:gap-4",
        !row.isOverridden && "opacity-70",
      )}
    >
      <div className="flex flex-col gap-1 sm:flex-1">
        <h3 className="text-body text-ink">{row.label}</h3>
        {row.isOverridden ? (
          <Button
            variant="link"
            size="sm"
            loading={isResetPending}
            onClick={onReset}
            data-testid={`pool-alert-reset-${row.alertKind}`}
          >
            {POOL_NOTIFICATIONS_RESET_LABEL}
          </Button>
        ) : (
          <p
            className="text-small text-ink-tertiary"
            data-testid={`pool-alert-caption-${row.alertKind}`}
          >
            {POOL_NOTIFICATIONS_FOLLOWING_CAPTION}
          </p>
        )}
      </div>
      <div className="flex flex-wrap gap-4">
        {row.cells.map((cell) => (
          <div
            key={cell.channel}
            data-testid={`pool-alert-${row.alertKind}-${cell.channel}`}
          >
            <Checkbox
              label={cell.label}
              checked={cell.isEnabled}
              disabled={
                cell.isLocked ||
                pendingChannel === cell.channel ||
                isResetPending
              }
              onCheckedChange={(next) => {
                onToggleChannel({
                  channel: cell.channel,
                  isEnabled: next === true,
                });
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};
